import { useEffect, useRef, type RefObject } from "react";

/**
 * 모달 접근성 공통 — Escape 닫기 + **뒤로 가기로 닫기** + 초기 포커스 이동(스크린리더·키보드).
 * 반환한 ref 를 dialog 컨테이너(role="dialog")에 붙인다. 내부 첫 포커서블에 포커스.
 * 마운트 1회만 동작(리렌더로 재포커스하지 않음). onClose 최신값은 ref 로 추적.
 */
export function useDialogA11y<T extends HTMLElement = HTMLDivElement>(
  onClose: () => void,
): RefObject<T> {
  const ref = useRef<T>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const me = {};
    escOrder.push(me);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // 🔴 덮개 위에 덮개가 있으면 **맨 위 것만** 닫는다 — 같은 document 에서 들어서 stopPropagation 으로는 서로 못 막는다
      if (escOrder[escOrder.length - 1] !== me) return;
      e.stopPropagation();
      closeRef.current();
    };
    document.addEventListener("keydown", onKey);
    const el = ref.current;
    const focusable = el?.querySelector<HTMLElement>(
      'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    (focusable ?? el)?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      const i = escOrder.lastIndexOf(me);
      if (i >= 0) escOrder.splice(i, 1);
    };
  }, []);

  useBackToClose(() => closeRef.current());

  return ref;
}

/** 우리가 쌓아 둔 뒤로가기용 칸인지 알아보는 표시. */
const MARK = "gcDialog";

/** Esc 를 들을 차례 — 늦게 열린 것이 위다. */
const escOrder: object[] = [];

/**
 * 🪟🪟 **열린 덮개들의 뒤로가기 칸 — 한 줄로 쌓는다**(2026-10-10).
 *
 * 예전엔 덮개마다 popstate 를 따로 들었다. 덮개 위에 덮개가 뜨면(굿즈 상세 → 「외부 사이트로 이동」)
 * 뒤로 한 번에 **둘 다** 닫혔고, 위 덮개가 ✕ 로 닫히며 제 칸을 빼려고 부른 `history.back()` 을
 * **아래 덮개가 제 「뒤로」로 알아들어** 같이 닫혔다 — 「취소」만 눌렀는데 굿즈 상세가 사라졌다.
 * 이제 popstate 는 한 곳에서 듣고 **맨 위 칸 하나**만 닫는다. 우리가 뺀 칸이 만든 popstate 는 센 만큼 흘려보낸다.
 */
interface BackEntry {
  close: () => void;
  pushed: boolean;
}
const backStack: BackEntry[] = [];
let ownPops = 0;
let popListening = false;
function onPopAny(): void {
  if (ownPops > 0) {
    ownPops--; // 덮개가 닫히며 제 칸을 뺀 것 — 누구의 「뒤로」도 아니다
    return;
  }
  for (let i = backStack.length - 1; i >= 0; i--) {
    const e = backStack[i]!;
    if (!e.pushed) continue; // 아직 칸을 안 쌓은 덮개(한 틱 미룸) — 그 아래 칸이 빠진 것이다
    e.pushed = false; // 그 칸은 이미 빠졌다 — 정리에서 또 빼지 않는다
    e.close();
    return;
  }
}

/**
 * 📱 **뒤로 가기로 덮개를 닫는다.**
 *
 * 🔴 폰에서 뒤로 가기는 아이가 제일 많이 쓰는 조작이다(안드로이드 뒤로 단추 · iOS 쓸어넘기기).
 * 그런데 덮개는 주소를 안 바꾸므로, 카드를 열어 둔 채 뒤로를 누르면 **덮개가 닫히는 게
 * 아니라 그 화면을 통째로 나간다** — 체험공간이라면 걷던 자리와 구역까지 잃는다
 * (2026-09-01 실측: 도서관에서 카드를 열고 뒤로 → 체험공간 밖).
 *
 * 그래서 덮개가 열릴 때 **같은 주소로 한 칸**을 쌓는다. 뒤로가 그 칸을 먹고 덮개만 닫힌다.
 *
 * ## 🔴 라우터의 자리 번호를 **이어서** 쌓는다
 * React Router 는 자기 상태를 `{usr, key, idx}` 로 넣어 둔다(`@remix-run/router`).
 * 그냥 `pushState({표시:true})` 로 덮으면 `idx` 가 사라져 라우터의 자리 계산이 어긋나고,
 * **뒤로 한 번에 두 칸** 물러난다(실측). 그래서 지금 상태를 그대로 두고 `idx` 만 하나 올린다.
 *
 * ## 🔴 ✕·Escape 로 닫을 때 그 칸을 도로 뺀다
 * 안 빼면 역사에 쓰레기가 쌓여 아이가 뒤로를 **두 번** 눌러야 앞 화면으로 간다.
 * 뒤로로 닫힌 경우엔 이미 빠졌으므로 다시 빼지 않는다 — 안 가리면 또 두 칸 물러난다.
 */
export function useBackToClose(onClose: () => void): void {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const h = typeof window === "undefined" ? null : window.history;
    if (!h?.pushState) return;
    const entry: BackEntry = { close: () => closeRef.current(), pushed: false };
    backStack.push(entry);
    if (!popListening) {
      window.addEventListener("popstate", onPopAny);
      popListening = true;
    }

    /**
     * 🔴 **쌓는 일을 다음 틱으로 미룬다.**
     *
     * 개발 빌드의 `StrictMode` 는 효과를 **두 번** 돌린다(마운트 → 정리 → 마운트).
     * 바로 쌓으면 가운데 정리에서 `history.back()` 이 걸리고, 그게 **뒤늦게** 도착해
     * 방금 다시 연 덮개를 닫아 버린다 — 개발에서 랠리 시트가 아예 안 떴다(e2e 가 잡았다).
     * 한 틱 미루면 StrictMode 의 헛 정리는 **아무것도 안 쌓은 채** 지나간다.
     */
    const t = window.setTimeout(() => {
      const cur = (h.state ?? {}) as Record<string, unknown>;
      const idx = typeof cur.idx === "number" ? cur.idx : 0;
      try {
        h.pushState({ ...cur, idx: idx + 1, [MARK]: true }, "");
        entry.pushed = true;
      } catch {
        /* 역사 API 를 못 쓰는 자리 — 뒤로 닫기만 없고 나머지는 그대로 돈다 */
      }
    }, 0);

    return () => {
      window.clearTimeout(t);
      const i = backStack.lastIndexOf(entry);
      if (i >= 0) backStack.splice(i, 1);
      if (entry.pushed && (window.history.state as Record<string, unknown> | null)?.[MARK] === true) {
        ownPops++; // 이 뒤로가 만드는 popstate 는 아래 덮개의 「뒤로」가 아니다
        window.history.back();
      }
    };
  }, []);
}
